import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import Alert from '../UI/Alert';
import LoadingIndicator from '../Common/LoadingIndicator';
import Card from '../UI/Card';
import TarefaStatusFilter from '../AgendaTarefas/TarefaStatusFilter';
import TarefaProfissionalFilter from '../AgendaTarefas/TarefaProfissionalFilter';
import PrimaryButton from '../Common/PrimaryButton';
import { Tag, User } from 'lucide-react';
import { useHistoricoCuidados } from '../../hooks/data/useHistoricoCuidados';

const PacienteHistoricoCuidadosTab = ({ pacienteId }) => {
	const { data, page, total, isLoading, error, setParams, setPage, refetch } =
		useHistoricoCuidados(pacienteId, { limit: 10, sortBy: 'data_hora', order: 'desc' });

	// Estados para filtros flutuantes
	const [filtroStatus, setFiltroStatus] = useState([]);
	const [filtroProfissionais, setFiltroProfissionais] = useState([]);
	
	const [showStatusFilter, setShowStatusFilter] = useState(false);
	const statusFilterRef = useRef(null);
	const statusButtonRef = useRef(null);
	
	const [showProfissionalFilter, setShowProfissionalFilter] = useState(false);
	const profissionalFilterRef = useRef(null);
	const profissionalButtonRef = useRef(null);

	// Fechar dropdowns ao clicar fora
	useEffect(() => {
		if (!showStatusFilter && !showProfissionalFilter) return;

		const handleClickOutside = (event) => {
			if (
				(showStatusFilter && statusFilterRef.current && statusButtonRef.current &&
					!statusFilterRef.current.contains(event.target) &&
					!statusButtonRef.current.contains(event.target)) ||
				(showProfissionalFilter && profissionalFilterRef.current && profissionalButtonRef.current &&
					!profissionalFilterRef.current.contains(event.target) &&
					!profissionalButtonRef.current.contains(event.target))
			) {
				setShowStatusFilter(false);
				setShowProfissionalFilter(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('touchstart', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('touchstart', handleClickOutside);
		};
	}, [showStatusFilter, showProfissionalFilter]);

	// Fechar dropdowns ao pressionar Escape
	useEffect(() => {
		const handleEscape = (event) => {
			if (event.key === 'Escape') {
				setShowStatusFilter(false);
				setShowProfissionalFilter(false);
			}
		};

		document.addEventListener('keydown', handleEscape);
		return () => {
			document.removeEventListener('keydown', handleEscape);
		};
	}, []);

	// Aplicar filtros aos dados exibidos
	const filteredData = useMemo(() => {
		if (!data) return [];
		
		let filtered = [...(data || [])];
		
		// Filtrar por profissional se selecionado
		if (filtroProfissionais && filtroProfissionais.length > 0) {
			const profIds = filtroProfissionais.map(id => id.toString());
			filtered = filtered.filter(item => {
				// Tentar encontrar o ID do profissional nos dados em diferentes campos possíveis
				const usuarioId = item.usuario_id || item.usuario_registro_id || item.usuario_id_registro || item.profissional_id;
				if (usuarioId) {
					return profIds.includes(usuarioId.toString());
				}
				// Se não tiver ID disponível, não filtrar esse item (mantém compatibilidade)
				return false;
			});
		}
		
		// Filtrar por status se selecionado
		if (filtroStatus && filtroStatus.length > 0) {
			filtered = filtered.filter(item => {
				const statusItem = item.status?.toLowerCase() || '';
				return filtroStatus.some(s => statusItem.includes(s.toLowerCase()));
			});
		}
		
		return filtered;
	}, [data, filtroStatus, filtroProfissionais]);

	const showingRange = useMemo(() => {
		const limit = 10;
		const filteredTotal = filteredData.length;
		const start = filteredTotal === 0 ? 0 : (page - 1) * limit + 1;
		const end = Math.min(page * limit, filteredTotal);
		return { start, end, limit, total: filteredTotal };
	}, [page, filteredData.length]);

	// Paginar dados filtrados
	const paginatedData = useMemo(() => {
		const limit = 10;
		const start = (page - 1) * limit;
		const end = start + limit;
		return filteredData.slice(start, end);
	}, [filteredData, page]);

	return (
		<div className="relative space-y-4">
			{/* Botões Flutuantes à Direita - Filtros */}
			<div className="sticky top-0 self-end z-30 flex flex-row gap-2 mb-4 w-fit ml-auto">
				{/* Botão de Status */}
				<div ref={statusButtonRef} className="relative">
					<PrimaryButton
						onClick={() => {
							setShowStatusFilter(!showStatusFilter);
							setShowProfissionalFilter(false);
						}}
						active={showStatusFilter}
						variant="primary"
						size="md"
						icon={<Tag className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${showStatusFilter ? 'scale-110' : ''} transition-transform`} />}
						title={showStatusFilter ? 'Ocultar filtro de status' : 'Mostrar filtro de status'}
					>
						<span className="hidden sm:inline whitespace-nowrap">Status</span>
					</PrimaryButton>
					{/* Badge indicando filtro ativo */}
					{filtroStatus && filtroStatus.length > 0 && (
						<span className="absolute -top-1 -right-1 h-4 w-4 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
							<span className="text-[8px] text-white font-bold">{filtroStatus.length}</span>
						</span>
					)}
					{/* Filtro de Status Flutuante - Dropdown */}
					{showStatusFilter && (
						<div ref={statusFilterRef} className="absolute top-full right-0 mt-2 z-[100] w-[calc(100vw-2rem)] sm:w-auto sm:min-w-[200px] max-h-[calc(100vh-8rem)] overflow-y-auto">
							<TarefaStatusFilter
								status={filtroStatus}
								onStatusChange={(newStatus) => {
									setFiltroStatus(newStatus);
									setPage(1); // Resetar para primeira página ao filtrar
								}}
								onClose={() => setShowStatusFilter(false)}
							/>
						</div>
					)}
				</div>

				{/* Botão de Profissional */}
				<div ref={profissionalButtonRef} className="relative">
					<PrimaryButton
						onClick={() => {
							setShowProfissionalFilter(!showProfissionalFilter);
							setShowStatusFilter(false);
						}}
						active={showProfissionalFilter}
						variant="primary"
						size="md"
						icon={<User className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${showProfissionalFilter ? 'scale-110' : ''} transition-transform`} />}
						title={showProfissionalFilter ? 'Ocultar filtro de profissional' : 'Mostrar filtro de profissional'}
					>
						<span className="hidden sm:inline whitespace-nowrap">Profissional</span>
					</PrimaryButton>
					{/* Badge indicando filtro ativo */}
					{filtroProfissionais && filtroProfissionais.length > 0 && (
						<span className="absolute -top-1 -right-1 h-4 w-4 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
							<span className="text-[8px] text-white font-bold">{filtroProfissionais.length}</span>
						</span>
					)}
					{/* Filtro de Profissional Flutuante - Dropdown */}
					{showProfissionalFilter && (
						<div ref={profissionalFilterRef} className="absolute top-full right-0 mt-2 z-[100] w-[calc(100vw-2rem)] sm:w-auto sm:min-w-[220px] max-h-[calc(100vh-8rem)] overflow-y-auto">
							<TarefaProfissionalFilter
								profissionais={filtroProfissionais}
								onProfissionaisChange={(newProfissionais) => {
									setFiltroProfissionais(newProfissionais);
									setPage(1); // Resetar para primeira página ao filtrar
								}}
								onClose={() => setShowProfissionalFilter(false)}
							/>
						</div>
					)}
				</div>
			</div>

			{isLoading && <LoadingIndicator message="Carregando histórico de cuidados..." />}
			{error && <Alert type="error" message={error} />}

			{!isLoading && !error && (
				<Card title={`Histórico de Cuidados (${showingRange.total} ${showingRange.total === 1 ? 'registro' : 'registros'})`}>
					{/* Desktop */}
					<div className="hidden md:block overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data/Hora</th>
									<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profissional</th>
									<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
									<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{paginatedData.map((item) => {
									const dateRaw = item.data_hora_registro || item.data_hora || item.data_hora_execucao || item.data_registro;
									const dateObj = dateRaw ? new Date(dateRaw) : null;
									const dateStr = dateObj && !isNaN(dateObj) ? dateObj.toLocaleString() : '-';
									const profissional = item.usuario_registro || item.profissional || item.profissional_nome || '-';
									const tipo = item.tipo_registro || item.tipo_cuidado || item.tipo_servico || '-';
									const descricao = item.observacoes || item.descricao || (item.conteudo_detalhado && item.conteudo_detalhado.origem ? item.conteudo_detalhado.origem : '-') ;
									return (
										<tr key={item.id}>
											<td className="px-4 py-2 text-sm text-gray-700">{dateStr}</td>
											<td className="px-4 py-2 text-sm text-gray-700">{profissional}</td>
											<td className="px-4 py-2 text-sm text-gray-700 capitalize">{tipo}</td>
											<td className="px-4 py-2 text-sm text-gray-700">{descricao}</td>
										</tr>
									);
								})}
							</tbody>
						</table>
						{filteredData.length === 0 && <p className="text-sm text-gray-500 p-4">Sem registros para os filtros aplicados.</p>}
					</div>

					{/* Mobile */}
					<div className="md:hidden space-y-3">
						{paginatedData.map((item) => {
							const dateRaw = item.data_hora_registro || item.data_hora || item.data_hora_execucao || item.data_registro;
							const dateObj = dateRaw ? new Date(dateRaw) : null;
							const dateStr = dateObj && !isNaN(dateObj) ? dateObj.toLocaleString() : '-';
							const profissional = item.usuario_registro || item.profissional || item.profissional_nome || '-';
							const tipo = item.tipo_registro || item.tipo_cuidado || item.tipo_servico || '-';
							const descricao = item.observacoes || item.descricao || (item.conteudo_detalhado && item.conteudo_detalhado.origem ? item.conteudo_detalhado.origem : '-') ;
							return (
								<div key={item.id} className="border rounded-lg p-3">
									<div className="text-xs text-gray-500">{dateStr}</div>
									<div className="text-sm font-medium text-gray-900">{profissional}</div>
									<div className="text-xs text-gray-600 capitalize">{tipo}</div>
									<div className="text-sm text-gray-700 mt-1">{descricao}</div>
								</div>
							);
						})}
						{filteredData.length === 0 && <p className="text-sm text-gray-500">Sem registros para os filtros aplicados.</p>}
					</div>

					{/* Paginação */}
					<div className="flex items-center justify-between mt-4">
						<p className="text-xs text-gray-500">
							Mostrando {showingRange.start}-{showingRange.end} de {showingRange.total}
						</p>
						<div className="flex gap-2">
							<button
								disabled={page <= 1}
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								className="px-3 py-1.5 rounded border text-sm disabled:opacity-50"
							>
								Anterior
							</button>
							<button
								disabled={showingRange.end >= showingRange.total}
								onClick={() => setPage((p) => p + 1)}
								className="px-3 py-1.5 rounded border text-sm disabled:opacity-50"
							>
								Próxima
							</button>
						</div>
					</div>
				</Card>
			)}
		</div>
	);
};

export default PacienteHistoricoCuidadosTab;

